import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Divider,
  Link,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LockIcon from "@mui/icons-material/Lock";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DoneIcon from "@mui/icons-material/Done";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import isMobile from "is-mobile";

const Section = styled(Paper)`
  border-radius: 12px !important;
  border: 1px solid #e8eaed;
  background: #fff;
  padding: 15px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const TitleIcon = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1a73e8;
  background: #e8f0fe;
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  padding: 12px 16px;
  border-top: 1px solid #f1f3f4;
  &:first-child {
    border-top: none;
  }
`;

// Caixa de observação igual ao print
const InfoNote = styled(Paper)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e8eaed;
  background: #f7f9fc;
  border-radius: 8px !important;
`;

// NOVOS estilos da seção de Assinatura
const TitleIconSuccess = styled(TitleIcon)`
  color: #16a34a;
  background: #dcfce7;
`;

const PriceBanner = styled(Paper)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-radius: 10px !important;
  border: 1px solid #bbf7d0;
  background: #ecfdf5;
`;

const SameCardBar = styled(Paper)`
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 10px !important;
  border: 1px solid #dbeafe;
  background: #eef5ff;
`;

function formatBRL(n: number) {
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

type CheckoutFormProps = {
  planMonthlyCost: number; // ex.: 799
};

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  planMonthlyCost,
}) => {
  // Adesão (mock)
  const baseAdesao = 500;

  // Estado geral
  const [agentCode, setAgentCode] = useState<string>(""); // inicia vazio
  const [agentFound, setAgentFound] = useState<null | { name: string }>(null);
  const [coupon, setCoupon] = useState<string>(""); // inicia vazio
  const [couponApplied, setCouponApplied] = useState(false);

  // Desconto do agente (modal + valor aplicado)
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [discountValueInput, setDiscountValueInput] = useState(""); // "0,00"
  const [discountReason, setDiscountReason] = useState("");
  const [agentDiscount, setAgentDiscount] = useState(0);
  const maxDiscount = baseAdesao - 0.01;

  // Abas do método de pagamento da Adesão
  const [tab, setTab] = useState<"card" | "pix">("card");

  // Campos do cartão (Adesão)
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [holder, setHolder] = useState("");

  // PIX
  const [expiresAt] = useState<number>(Date.now() + 30 * 60 * 1000); // agora + 30min
  const [remaining, setRemaining] = useState<number>(expiresAt - Date.now());
  const pixCode =
    "00020101021226BR.GOV.BCB.PIX2563qrcode.mock.georanking/txid/12345678952040000530398654041.005802BR5920GeoRanking LTDA6009SAO PAULO62070503***6304ABCD";

  useEffect(() => {
    const id = setInterval(
      () => setRemaining(Math.max(0, expiresAt - Date.now())),
      1000
    );
    return () => clearInterval(id);
  }, [expiresAt]);

  const mm = Math.floor(remaining / 1000 / 60)
    .toString()
    .padStart(2, "0");
  const ss = Math.floor((remaining / 1000) % 60)
    .toString()
    .padStart(2, "0");

  // Handlers
  const handleSearchAgent = () => {
    // Mock: código válido se começar com "123"
    if (agentCode.replace(/\D/g, "").startsWith("123")) {
      setAgentFound({ name: "João Silva - Representante BH" });
    } else {
      setAgentFound(null);
    }
  };

  const handleApplyCoupon = () => {
    // Mock: cupom válido = "FRANQUIAS"
    setCouponApplied(coupon.trim().toUpperCase() === "FRANQUIAS");
  };

  const adesaoTotalHoje = useMemo(() => {
    if (couponApplied) return 0;
    return Math.max(baseAdesao - agentDiscount, 0);
  }, [couponApplied, agentDiscount]);

  const handleConfirmDiscount = () => {
    const value = parseFloat(
      discountValueInput.replace(/\./g, "").replace(",", ".")
    );
    if (
      !isNaN(value) &&
      value > 0 &&
      value <= maxDiscount &&
      discountReason.trim()
    ) {
      setAgentDiscount(Number(value.toFixed(2)));
      setIsDiscountModalOpen(false);
    }
  };

  // estado Assinatura
  const [useSameCard, setUseSameCard] = useState(true);
  const [isCancelPolicyOpen, setIsCancelPolicyOpen] = useState(false);
  const [isBillingCycleOpen, setIsBillingCycleOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const handleOpenWhatsApp = () => {
    const msg = encodeURIComponent("Olá! Preciso de suporte com o pagamento.");
    window.open(`https://wa.me/558008001700?text=${msg}`, "_blank");
  };
  const handleCallNow = () => {
    window.location.href = "tel:08008001700";
  };

  // Cartão exclusivo da Assinatura (segundo cartão)
  const [subCardNumber, setSubCardNumber] = useState("");
  const [subExpiry, setSubExpiry] = useState("");
  const [subCvc, setSubCvc] = useState("");
  const [subHolder, setSubHolder] = useState("");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Adesão (Agente) */}
      <Section variant="outlined">
        <SectionHeader>
          <TitleIcon>
            <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 18 }} />
          </TitleIcon>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Adesão (Agente)
          </Typography>
        </SectionHeader>
        <Typography variant="body2" sx={{ color: "#5f6368", mb: 1 }}>
          A Adesão é única e vai para o seu agente
        </Typography>

        <Grid container columnSpacing={3} rowSpacing={0.5}>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ width: isMobile() ? "100%" : "inherit" }}
          >
            <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
              <TextField
                size="small"
                label="Código do Agente"
                required
                fullWidth
                value={agentCode}
                onChange={(e) => setAgentCode(e.target.value)}
                placeholder="#123"
              />
              <Button
                size="small"
                onClick={handleSearchAgent}
                variant="outlined"
                color="primary"
                startIcon={<SearchIcon sx={{ marginLeft: "12px" }} />}
                sx={{ minWidth: 44, px: 1 }}
              />
            </Box>
            <Box sx={{ mt: isMobile() ? -2 : 0.25, minHeight: 22 }}>
              {agentFound ? (
                <Chip
                  color="success"
                  variant="outlined"
                  icon={<DoneIcon />}
                  label={`${agentFound.name} encontrado`}
                  size="small"
                />
              ) : agentCode ? (
                <Chip
                  color="warning"
                  variant="outlined"
                  icon={<ErrorOutlineIcon />}
                  label="Informe e busque o código do seu agente"
                  size="small"
                />
              ) : null}
            </Box>
          </Grid>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{ width: isMobile() ? "100%" : "inherit" }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="Cupom de Desconto"
                fullWidth
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="FRANQUIAS"
              />
              <Button
                size="small"
                onClick={handleApplyCoupon}
                variant="outlined"
                color="primary"
                sx={{ whiteSpace: "nowrap" }}
              >
                Aplicar
              </Button>
            </Box>
            <Box sx={{ mt: 0.25, minHeight: 22 }}>
              {couponApplied && (
                <Chip
                  color="success"
                  variant="outlined"
                  icon={<DoneIcon />}
                  label="Adesão isenta!"
                  size="small"
                />
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Resumo de preço */}
        <Paper
          variant="outlined"
          sx={{ mt: 0, borderRadius: 2, overflow: "hidden" }}
        >
          <SummaryHeader>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "#1a73e8",
                fontWeight: 700,
              }}
            >
              <LocalOfferOutlinedIcon fontSize="small" />
              Resumo de preço
            </Typography>
            <Link
              component="button"
              underline="hover"
              sx={{ fontSize: 14 }}
              onClick={() => setIsDiscountModalOpen(true)}
            >
              Aplicar Desconto
            </Link>
          </SummaryHeader>
          <Divider />
          {/* Valor base sem chip */}
          <SummaryRow>
            <Typography variant="body2" sx={{ color: "#5f6368" }}>
              Valor base
            </Typography>
            <Typography variant="body2">{formatBRL(baseAdesao)}</Typography>
          </SummaryRow>

          {/* Quando houver desconto, mostrar o chip no lugar do texto "Desconto aplicado" */}
          {agentDiscount > 0 && (
            <SummaryRow>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label="Desconto do agente"
                  sx={{ height: 22 }}
                  icon={
                    <Tooltip title="Desconto aplicado conforme acordo com o agente">
                      <InfoOutlinedIcon fontSize="small" />
                    </Tooltip>
                  }
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "#1a73e8", fontWeight: 600 }}
              >
                - {formatBRL(agentDiscount)}
              </Typography>
            </SummaryRow>
          )}
          <SummaryRow>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Total de hoje
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {formatBRL(adesaoTotalHoje)}
              </Typography>
              {couponApplied && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label="ISENTO"
                />
              )}
            </Box>
          </SummaryRow>
        </Paper>

        {/* Método de pagamento da Adesão */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Método de Pagamento da Adesão
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#fafbff",
            borderColor: "#e8eaed",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            centered
          >
            <Tab
              value="card"
              label="Cartão"
              icon={<CreditCardIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              value="pix"
              label="PIX"
              icon={<QrCode2Icon fontSize="small" />}
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {tab === "card" ? (
              <Box sx={{ display: "grid", gap: 1.5 }}>
                {/* Número do cartão - linha 1 */}
                <TextField
                  size="small"
                  required
                  label="Número do Cartão"
                  fullWidth
                  placeholder="1234 5678 9012 3456"
                  value={cardNumber}
                  onChange={(e) =>
                    setCardNumber(
                      e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 16)
                        .replace(/(\d{4})(?=\d)/g, "$1 ")
                    )
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CreditCardIcon sx={{ color: "#9e9e9e" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />

                {/* Validade e CVV - linha 2 */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    size="small"
                    required
                    label="Validade"
                    fullWidth
                    placeholder="MM/AA"
                    value={expiry}
                    onChange={(e) => {
                      const only = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 4);
                      const mm = only.slice(0, 2);
                      const aa = only.slice(2, 4);
                      setExpiry([mm, aa].filter(Boolean).join("/"));
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                  <TextField
                    size="small"
                    required
                    label="CVV"
                    fullWidth
                    placeholder="123"
                    value={cvc}
                    onChange={(e) =>
                      setCvc(e.target.value.replace(/\D/g, "").slice(0, 3))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <LockIcon sx={{ color: "#9e9e9e" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  />
                </Box>

                {/* Nome do titular - linha 3 */}
                <TextField
                  size="small"
                  required
                  label="Nome do Titular"
                  fullWidth
                  placeholder="Nome como está no cartão"
                  value={holder}
                  onChange={(e) => setHolder(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                />

                {/* Nota */}
                <InfoNote elevation={0}>
                  <EventAvailableIcon sx={{ color: "#5f6368" }} />
                  <Typography variant="body2" sx={{ color: "#5f6368" }}>
                    A NF da Adesão será emitida pelo seu agente.
                  </Typography>
                </InfoNote>
              </Box>
            ) : (
              <Box
                sx={{
                  border: "1px dashed #c6dafc",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  bgcolor: "#f6faff",
                }}
              >
                <Box
                  sx={{
                    width: 260,
                    height: 220,
                    borderRadius: 2,
                    bgcolor: "#eef2f7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <QrCode2Icon sx={{ fontSize: 120, color: "#9aa0a6" }} />
                </Box>

                <Typography variant="body2" sx={{ color: "#5f6368" }}>
                  Escaneie o QR Code com o app do seu banco
                </Typography>

                <Typography
                  variant="h5"
                  sx={{ color: "#1a73e8", fontWeight: 800, my: 1 }}
                >
                  {mm}:{ss}
                </Typography>
                <Typography variant="caption" sx={{ color: "#5f6368" }}>
                  Código expira em
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => navigator.clipboard.writeText(pixCode)}
                  >
                    Copiar código PIX
                  </Button>
                  <Button variant="text" onClick={() => setTab("card")}>
                    Trocar para cartão
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Modal de Desconto do Agente */}
        <Dialog
          open={isDiscountModalOpen}
          onClose={() => setIsDiscountModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Aplicar Desconto do Agente
            <IconButton
              aria-label="close"
              onClick={() => setIsDiscountModalOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ color: "#5f6368", mb: 2 }}>
              Informe o valor do desconto e o motivo obrigatório para registro
              de auditoria
            </Typography>
            <TextField
              label="Valor do Desconto (R$) *"
              fullWidth
              placeholder="0"
              value={discountValueInput}
              onChange={(e) => {
                // mantém apenas dígitos
                const onlyDigits = e.target.value.replace(/\D/g, "");
                setDiscountValueInput(onlyDigits);
              }}
              inputMode="numeric"
              onKeyDown={(e) => {
                const allowed = [
                  "Backspace",
                  "Delete",
                  "ArrowLeft",
                  "ArrowRight",
                  "Tab",
                ];
                if (!/^\d$/.test(e.key) && !allowed.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              sx={{ mb: 1 }}
            />
            <Typography
              variant="caption"
              sx={{ color: "#5f6368", display: "block", mb: 2 }}
            >
              Máximo: {formatBRL(maxDiscount)}
            </Typography>
            <TextField
              label="Motivo do Desconto *"
              fullWidth
              multiline
              minRows={3}
              placeholder="Ex: Cliente fidelizado, campanha especial, parceria estratégica..."
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              helperText="Este motivo será registrado para auditoria interna"
            />
          </DialogContent>
          <DialogActions sx={{ p: "16px 24px" }}>
            <Button
              variant="outlined"
              onClick={() => setIsDiscountModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmDiscount}
              disabled={
                !discountReason.trim() ||
                !discountValueInput.trim() ||
                isNaN(
                  parseFloat(
                    discountValueInput.replace(/\./g, "").replace(",", ".")
                  )
                ) ||
                parseFloat(
                  discountValueInput.replace(/\./g, "").replace(",", ".")
                ) <= 0 ||
                parseFloat(
                  discountValueInput.replace(/\./g, "").replace(",", ".")
                ) > maxDiscount
              }
            >
              Aplicar Desconto
            </Button>
          </DialogActions>
        </Dialog>
      </Section>

      {/* Assinatura (GeoRanking) */}
      <Section variant="outlined" sx={{ marginBottom: "8vh" }}>
        <SectionHeader>
          <TitleIconSuccess>
            <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
          </TitleIconSuccess>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Assinatura (GeoRanking)
          </Typography>
        </SectionHeader>
        <Typography variant="body2" sx={{ color: "#5f6368", mb: 1 }}>
          Sua assinatura será recorrente no cartão
        </Typography>

        <PriceBanner elevation={0}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#065f46" }}>
              {formatBRL(planMonthlyCost)}/mês
            </Typography>
            <Typography variant="body2" sx={{ color: "#047857" }}>
              1ª cobrança em 01 de dezembro de 2025
            </Typography>
          </Box>
          <CalendarMonthOutlinedIcon sx={{ color: "#16a34a" }} />
        </PriceBanner>

        <SameCardBar elevation={0}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={useSameCard}
                onChange={(e) => setUseSameCard(e.target.checked)}
              />
            }
            label="Usar o mesmo cartão da Adesão para a Assinatura"
          />
        </SameCardBar>

        {/* Form de cartão para Assinatura (segundo cartão) */}
        {!useSameCard && (
          <Paper
            variant="outlined"
            sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "#fff" }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Cartão para Assinatura
            </Typography>

            <Box sx={{ display: "grid", gap: 1.5 }}>
              {/* Número do cartão */}
              <TextField
                size="small"
                required
                label="Número do Cartão"
                placeholder="1234 5678 9012 3456"
                value={subCardNumber}
                onChange={(e) =>
                  setSubCardNumber(
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 16)
                      .replace(/(\d{4})(?=\d)/g, "$1 ")
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CreditCardIcon sx={{ color: "#9e9e9e" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                fullWidth
              />

              {/* Validade e CVV */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"
                  required
                  label="Validade"
                  placeholder="MM/AA"
                  value={subExpiry}
                  onChange={(e) => {
                    const only = e.target.value.replace(/\D/g, "").slice(0, 4);
                    const mm = only.slice(0, 2);
                    const aa = only.slice(2, 4);
                    setSubExpiry([mm, aa].filter(Boolean).join("/"));
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  fullWidth
                />
                <TextField
                  size="small"
                  required
                  label="CVV"
                  placeholder="123"
                  value={subCvc}
                  onChange={(e) =>
                    setSubCvc(e.target.value.replace(/\D/g, "").slice(0, 3))
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon sx={{ color: "#9e9e9e" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                  fullWidth
                />
              </Box>

              {/* Nome do titular */}
              <TextField
                size="small"
                required
                label="Nome do Titular"
                placeholder="Nome como está no cartão"
                value={subHolder}
                onChange={(e) => setSubHolder(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                fullWidth
              />
            </Box>
          </Paper>
        )}

        <Box sx={{ display: "flex", gap: 3, mt: 2, flexWrap: "wrap" }}>
          <Link
            component="button"
            underline="hover"
            sx={{ fontSize: 14 }}
            onClick={() => setIsCancelPolicyOpen(true)}
          >
            Política de cancelamento
          </Link>
          <span style={{ color: "#9aa0a6" }}>·</span>
          <Link
            component="button"
            underline="hover"
            sx={{ fontSize: 14 }}
            onClick={() => setIsBillingCycleOpen(true)}
          >
            Ciclo de cobrança
          </Link>
          <span style={{ color: "#9aa0a6" }}>·</span>
          <Link
            component="button"
            underline="hover"
            sx={{ fontSize: 14 }}
            onClick={() => setIsSupportOpen(true)}
          >
            Suporte
          </Link>
        </Box>

        {/* Modal: Política de Cancelamento */}
        <Dialog
          open={isCancelPolicyOpen}
          onClose={() => setIsCancelPolicyOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800 }}>
            Política de Cancelamento
            <IconButton
              aria-label="close"
              onClick={() => setIsCancelPolicyOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ mb: 2, color: "#5f6368" }}>
              Você pode cancelar a qualquer momento. Reembolso integral em até 7
              dias da adesão.
            </Typography>

            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "#fff", mb: 2 }}
            >
              <Box sx={{ display: "grid", gap: 1.5 }}>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <CheckCircleOutlineIcon sx={{ color: "#16a34a" }} />
                  <Typography variant="body2">
                    Cancelamento a qualquer momento pelos canais oficiais da
                    plataforma (app/WhatsApp/telefone).
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <CheckCircleOutlineIcon sx={{ color: "#16a34a" }} />
                  <Typography variant="body2">
                    Reembolso integral da primeira cobrança da ADESÃO quando
                    solicitado em até 7 (sete) dias corridos da contratação
                    (direito de arrependimento – CDC).
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <InfoOutlinedIcon sx={{ color: "#1a73e8" }} />
                  <Typography variant="body2">
                    A ADESÃO é recebida diretamente pelo agente e o estorno,
                    quando cabível, é realizado por ele no mesmo meio de
                    pagamento utilizado.
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <InfoOutlinedIcon sx={{ color: "#1a73e8" }} />
                  <Typography variant="body2">
                    A MENSALIDADE é cobrada pelo GeoRanking. Após o prazo de 7
                    dias, valores do ciclo vigente não são reembolsáveis quando
                    o serviço já foi disponibilizado/executado.
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <ErrorOutlineIcon sx={{ color: "#ea580c" }} />
                  <Typography variant="body2">
                    Prazo de estorno: o crédito é solicitado no mesmo método de
                    pagamento; a disponibilidade depende do emissor do
                    cartão/banco.
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <WarningAmberOutlinedIcon sx={{ color: "#f59e0b" }} />
                  <Typography variant="body2">
                    Após o cancelamento, a gestão do perfil é suspensa e
                    eventuais impulsionamentos/anúncios podem ser removidos em
                    até 15 dias.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#f8fbff",
                borderColor: "#dbeafe",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PhoneInTalkOutlinedIcon sx={{ color: "#1a73e8" }} />
              <Typography variant="body2">
                Para cancelar, entre em contato pelo{" "}
                <strong>0800 800 1700</strong>
              </Typography>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ p: "16px 24px" }}>
            <Button
              variant="contained"
              onClick={() => setIsCancelPolicyOpen(false)}
            >
              Entendi
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal: Ciclo de Cobrança */}
        <Dialog
          open={isBillingCycleOpen}
          onClose={() => setIsBillingCycleOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 800 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "#16a34a" }} />
              Ciclo de Cobrança
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setIsBillingCycleOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ mb: 2, color: "#5f6368" }}>
              Assinatura mensal no cartão, com renovação automática até
              cancelamento.
            </Typography>

            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, bgcolor: "#fff" }}
            >
              <Box sx={{ display: "grid", gap: 1.5 }}>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <CheckCircleOutlineIcon sx={{ color: "#16a34a" }} />
                  <Typography variant="body2">
                    A assinatura é <strong>recorrente MENSAL no CARTÃO</strong>{" "}
                    de crédito salvo, com cobrança automática até que o cliente
                    solicite cancelamento.
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <CalendarMonthOutlinedIcon sx={{ color: "#1a73e8" }} />
                  <Typography variant="body2">
                    A <strong>data de renovação</strong> corresponde à data da
                    primeira ativação, podendo variar por feriados/operadoras.
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <ErrorOutlineIcon sx={{ color: "#ea580c" }} />
                  <Typography variant="body2">
                    Em caso de <strong>falha no pagamento</strong> (recusa,
                    limite, chargeback etc.), os serviços podem ser suspensos
                    até a regularização; retentativas automáticas podem ocorrer.
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <LocalOfferOutlinedIcon sx={{ color: "#7e22ce" }} />
                  <Typography variant="body2">
                    <strong>Período de cortesia</strong> (quando ofertado por
                    cupom): até 30 dias. Mesmo na cortesia, é necessário
                    cadastrar um cartão para ativação da cobrança automática ao
                    final, caso não haja cancelamento.
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <WarningAmberOutlinedIcon sx={{ color: "#f59e0b" }} />
                  <Typography variant="body2">
                    <strong>Alterações de preço ou de condições</strong> serão
                    comunicadas com antecedência mínima de 7 dias.
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                >
                  <CreditCardIcon sx={{ color: "#5f6368" }} />
                  <Typography variant="body2">
                    O cliente pode <strong>alterar o cartão</strong> a qualquer
                    momento na área de cobrança/assinatura.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: "#ecfdf5",
                borderColor: "#bbf7d0",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircleOutlineIcon sx={{ color: "#16a34a" }} />
              <Typography variant="body2">
                Sua próxima cobrança: <strong>01 de dezembro de 2025</strong>
              </Typography>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ p: "16px 24px" }}>
            <Button
              variant="contained"
              onClick={() => setIsBillingCycleOpen(false)}
            >
              Entendi
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal: Suporte */}
        <Dialog
          open={isSupportOpen}
          onClose={() => setIsSupportOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SupportAgentIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Suporte
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setIsSupportOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ color: "#5f6368", mb: 2 }}>
              Suporte de 2ª a 6ª, 9h–18h (BRT), por telefone e WhatsApp.
            </Typography>

            {/* Card com telefone */}
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 2,
                bgcolor: "#f5f3ff",
                borderColor: "#e9d5ff",
                textAlign: "center",
              }}
            >
              <SupportAgentIcon
                sx={{ color: "#7c3aed", fontSize: 28, mb: 1 }}
              />
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, color: "#5b21b6" }}
              >
                0800 800 1700
              </Typography>
              <Typography variant="body2" sx={{ color: "#5f6368" }}>
                Telefone e WhatsApp
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <AccessTimeIcon fontSize="small" sx={{ color: "#7c3aed" }} />
                <Typography variant="caption" sx={{ color: "#5f6368" }}>
                  Segunda a Sexta, 9h às 18h (Horário de Brasília)
                </Typography>
              </Box>
            </Paper>

            {/* Regras/Notas */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: "grid", gap: 1.25 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <AccessTimeIcon sx={{ color: "#1a73e8" }} />
                  <Typography variant="body2">
                    <strong>Horário:</strong> segunda a sexta, das 9h às 18h
                    (horário de Brasília).
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <PhoneInTalkOutlinedIcon sx={{ color: "#1a73e8" }} />
                  <Typography variant="body2">
                    <strong>Canais oficiais:</strong> telefone e WhatsApp no
                    número <strong>0800 800 1700</strong>.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <InfoOutlinedIcon sx={{ color: "#1a73e8" }} />
                  <Typography variant="body2">
                    <strong>SLA de primeira resposta:</strong> até 72 horas
                    úteis, conforme a complexidade do chamado.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <WarningAmberOutlinedIcon sx={{ color: "#ea580c" }} />
                  <Typography variant="body2">
                    <strong>Prioridade:</strong> solicitações de acesso,
                    cobrança, cancelamento e indisponibilidade recebem
                    prioridade de atendimento.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <VerifiedUserOutlinedIcon sx={{ color: "#16a34a" }} />
                  <Typography variant="body2">
                    Para segurança, podemos solicitar{" "}
                    <strong>validação de identidade</strong> e dados do contrato
                    antes de executar alterações.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <AssignmentOutlinedIcon sx={{ color: "#7c3aed" }} />
                  <Typography variant="body2">
                    Registre o <strong>número do contrato/assinatura</strong> e
                    o <strong>código do agente</strong>
                    (se aplicável) para agilizar o atendimento.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Ações */}
            <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={handleOpenWhatsApp}
              >
                Abrir WhatsApp
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhoneInTalkOutlinedIcon />}
                onClick={handleCallNow}
              >
                Ligar Agora
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: "16px 24px" }}>
            <Button onClick={() => setIsSupportOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>
      </Section>
    </Box>
  );
};

export default CheckoutForm;
